// components/SettingsPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "https://worthy-optimism-production.up.railway.app/api";

const SettingsPage = () => {
  const { currentUser, logout, token } = useAuth(); // Assuming token is available via useAuth
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false); // For password change
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userStats, setUserStats] = useState(null); // For queryCount, queryLimit, subscriptionType
  const [subscriptionDetails, setSubscriptionDetails] = useState(null); // For name, startDate, endDate from API

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !token) {
        setPageLoading(false);
        // Optionally navigate to login or show message if user/token not available
        return;
      }
      setPageLoading(true);
      try {
        const config = { headers: { "Authorization": `Bearer ${token}` } };
        const response = await axios.get(`${API_URL}/subscription/stats`, config);
        if (response.data) {
          setUserStats({
            queryCount: response.data.queryCount === undefined ? 0 : response.data.queryCount,
            queryLimit: response.data.queryLimit,
            subscriptionType: response.data.subscriptionType
          });
          // Set subscriptionDetails from the 'subscription' object in the response
          if (response.data.subscription) {
            setSubscriptionDetails(response.data.subscription);
          } else {
            setSubscriptionDetails(null);
          }
        } else {
          setUserStats(null);
          setSubscriptionDetails(null);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data.");
        setUserStats(null);
        setSubscriptionDetails(null);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [currentUser, token]); // Depend on currentUser and token

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (newPassword !== confirmPassword) {
      return setError("New passwords do not match");
    }
    if (newPassword.length < 6) {
      return setError("Password must be at least 6 characters long");
    }
    setLoading(true);
    try {
      const config = { headers: { "Authorization": `Bearer ${token}` } };
      const response = await axios.post(`${API_URL}/auth/change-password`, {
        currentPassword,
        newPassword
      }, config);
      if (response.data.success) {
        setSuccess("Password updated successfully");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(response.data.error || "Failed to change password");
      }
    } catch (err) {
      console.error("Error changing password:", err);
      setError(err.response?.data?.error || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const canChangePassword = currentUser && currentUser.authProvider === "email";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("bs", {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(date);
  };

  const getSubscriptionStatus = () => {
    if (!userStats) return "Učitavanje...";
    if (currentUser && currentUser.roles && currentUser.roles.includes("admin")) {
      return "Administrator (neograničen pristup)";
    }
    if (userStats.subscriptionType === "unlimited") {
      return "Neograničen plan";
    } else if (userStats.subscriptionType === "standard") {
      return `Standardni plan: ${userStats.queryCount}/${userStats.queryLimit || "N/A"} upita iskorišteno`;
    } else if (userStats.subscriptionType === "free") {
      return `Besplatna verzija: ${userStats.queryCount}/3 upita iskorišteno`;
    } else {
        return `Besplatna verzija: ${userStats.queryCount}/3 upita iskorišteno. Potrebna pretplata.`;
    }
  };

  if (pageLoading) {
    return <div className="loading-indicator" style={{ textAlign: "center", padding: "4rem" }}><p>Učitavanje postavki...</p></div>;
  }

  return (
    <div className="settings-container">
      <div className="settings-card account-info">
        <h2 className="settings-title">Korisnički račun</h2>
        <div className="account-details">
          {currentUser && (
            <>
              <div className="account-detail-item">
                <label>Email:</label>
                <div>{currentUser.email}</div>
              </div>
              <div className="account-detail-item">
                <label>Ime:</label>
                <div>{currentUser.fullName || "Nije uneseno"}</div>
              </div>
              <div className="account-detail-item">
                <label>Tip računa:</label>
                <div>
                  {currentUser.authProvider === "google" ? "Google račun" : "Email račun"}
                </div>
              </div>
              <div className="account-detail-item">
                <label>Pretplata:</label>
                <div className="subscription-status-text">
                  {getSubscriptionStatus()}
                </div>
              </div>
              {subscriptionDetails && subscriptionDetails.endDate && (
                <div className="account-detail-item">
                  <label>Ističe:</label>
                  <div>{formatDate(subscriptionDetails.endDate)}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {canChangePassword && (
        <div className="settings-card password-change">
           <h2 className="settings-title">Promjena lozinke</h2>
          <form onSubmit={handlePasswordChange} className="password-form">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <div className="form-group">
              <label htmlFor="currentPassword">Trenutna lozinka</label>
              <input type="password" id="currentPassword" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">Nova lozinka</label>
              <input type="password" id="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Potvrda nove lozinke</label>
              <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? "Izmjena u toku..." : "Promijeni lozinku"}
            </button>
          </form>
        </div>
      )}

      <div className="settings-card subscription-info">
        <h2 className="settings-title">Pretplata</h2>
        <div className="subscription-details">
          {currentUser && currentUser.roles && currentUser.roles.includes("admin") ? (
            <p>Imate administratorski račun s neograničenim pristupom.</p>
          ) : subscriptionDetails ? (
            <>
              <div className="subscription-plan">
                <span className="plan-name">
                  {subscriptionDetails.name || (userStats?.subscriptionType === "unlimited" ? "Neograničen plan" : "Standardni plan")}
                </span>
                <div className="subscription-dates">
                  {subscriptionDetails.startDate && <div>Aktivna od: {formatDate(subscriptionDetails.startDate)}</div>}
                  {subscriptionDetails.endDate && <div>Ističe: {formatDate(subscriptionDetails.endDate)}</div>}
                </div>
              </div>
              {userStats && userStats.subscriptionType === "standard" && (
                <div className="usage-progress-container">
                  <div className="usage-info">
                    <span>Iskorišteni upiti: {userStats.queryCount} od {userStats.queryLimit || "N/A"}</span>
                    <div className="usage-progress">
                      <div
                        className="usage-progress-bar standard"
                        style={{ width: `${Math.min(100, (userStats.queryCount / (userStats.queryLimit || 1)) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
              <button
                className="button primary upgrade-button"
                onClick={() => window.location.href = "mailto:fbrkovic@gmail.com?subject=Upit%20za%20pretplatu&body=Želim%20se%20pretplatiti%20na%20AI%20Poreznik.%20Molim%20vas%20više%20informacija."}
              >
                Nadogradi pretplatu
              </button>
            </>
          ) : userStats && (userStats.subscriptionType === "free" || userStats.subscriptionType === null) ? (
            <>
              <p>Trenutno koristite besplatnu verziju.</p>
              <div className="usage-progress-container">
                <div className="usage-info">
                  <span>Iskorišteni upiti: {userStats.queryCount} od 3</span>
                  <div className="usage-progress">
                    <div
                      className="usage-progress-bar free"
                      style={{ width: `${Math.min(100, (userStats.queryCount / 3) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <button
                className="button primary upgrade-button"
                onClick={() => window.location.href = "mailto:fbrkovic@gmail.com?subject=Upit%20za%20pretplatu&body=Želim%20se%20pretplatiti%20na%20AI%20Poreznik.%20Molim%20vas%20više%20informacija."}
              >
                Pretplati se
              </button>
            </>
          ) : (
            <p>Učitavanje informacija o pretplati...</p>
          )}
        </div>
      </div>

      <div className="settings-actions">
        <button
          className="button danger-button"
          onClick={() => {
            if (window.confirm("Jeste li sigurni da se želite odjaviti?")) {
              logout();
              navigate("/app/login");
            }
          }}
        >
          Odjava
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;

